import { useCallback, useRef } from "react"
import { useDrawingArea, useSvgRef } from "@mui/x-charts/hooks"

import useEventListeners from "helpers/hooks/useEventListeners"
import { useHoverSync } from "helpers/hooks/useHoverSync"

interface ChartHoverBridgeProps {
  xDomainMin: number
  xDomainMax: number
}

/**
 * Attaches pointer listeners to the chart SVG and publishes the hovered/touched
 * timestamp to HoverSyncContext synchronously — no React state, no re-renders.
 *
 * Pointer events unify mouse, touch and pen, so the crosshair follows the cursor
 * on desktop and the finger on touch devices. Touch panning on the shared SVG
 * comes from ChartsSurface, which sets `touch-action: pan-y`.
 *
 * Must be rendered as a direct child of chart components.
 * Renders nothing.
 */
const ChartHoverBridge = ({ xDomainMin, xDomainMax }: ChartHoverBridgeProps) => {
  const { publish } = useHoverSync()
  const svgRef = useSvgRef()
  const drawingArea = useDrawingArea()

  // Keep latest domain/area in a ref so event handlers never go stale.
  const stateRef = useRef({ drawingArea, xDomainMin, xDomainMax, publish })
  stateRef.current = { drawingArea, xDomainMin, xDomainMax, publish }

  const handlePointerMove = useCallback(
    (e: PointerEvent) => {
      const svg = svgRef.current
      if (!svg) return
      const {
        drawingArea: da,
        xDomainMin: min,
        xDomainMax: max,
        publish: pub,
      } = stateRef.current

      const rect = svg.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top

      // Ignore positions outside drawing area
      if (x < da.left || x > da.left + da.width || y < da.top || y > da.top + da.height) {
        pub(null)
        return
      }

      const range = max - min
      if (range === 0 || !isFinite(range)) return

      const timestamp = min + ((x - da.left) / da.width) * range
      const yProportion = (y - da.top) / da.height
      pub({ x: timestamp, yProportion })
    },
    [svgRef],
  )

  // Clear the crosshair when the pointer leaves the chart: the cursor exits the
  // SVG (desktop) or the finger lifts / the gesture is cancelled (touch).
  const handlePointerEnd = useCallback(() => {
    stateRef.current.publish(null)
  }, [])

  useEventListeners(svgRef, {
    pointermove: handlePointerMove,
    pointerleave: handlePointerEnd,
    pointercancel: handlePointerEnd,
  })

  return null
}

export default ChartHoverBridge
